// Singleton WebSocket client for instant CAD⇄MDT sync + live presence/typing.
// Server pushes {type:"invalidate",topics:[...]} refresh pings AND relays
// {type:"presence"|"typing",...} events between clients in the same env.
// Polling stays in place as a safety net, so nothing regresses if WS is down.
import { useEffect, useRef } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL;
// Per-tab id so a client can ignore the echo of its own presence/typing events.
export const CLIENT_ID = Math.random().toString(36).slice(2) + Date.now().toString(36);

function wsUrl() {
  const token = localStorage.getItem("sentinel_token") || "";
  const u = new URL(BASE);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws";
  u.search = `?token=${encodeURIComponent(token)}`;
  return u.toString();
}

let socket = null;
let heartbeat = null;
let reconnectT = null;
let backoff = 1000;
const subs = new Set();      // invalidate subscribers: { topics:Set<string>, cb }
const eventSubs = new Set(); // raw presence/typing subscribers: cb(msg)

function hasWork() { return subs.size > 0 || eventSubs.size > 0; }
function cleanupTimers() { if (heartbeat) { clearInterval(heartbeat); heartbeat = null; } }

function scheduleReconnect() {
  if (reconnectT || !hasWork()) return;
  reconnectT = setTimeout(() => { reconnectT = null; if (hasWork()) connect(); }, backoff);
  backoff = Math.min(backoff * 2, 15000);
}

function connect() {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  if (!localStorage.getItem("sentinel_token")) return;
  let s;
  try { s = new WebSocket(wsUrl()); } catch { scheduleReconnect(); return; }
  socket = s;
  s.onopen = () => {
    backoff = 1000;
    cleanupTimers();
    heartbeat = setInterval(() => { try { if (s.readyState === WebSocket.OPEN) s.send("ping"); } catch { /* noop */ } }, 25000);
  };
  s.onmessage = (ev) => {
    let msg;
    try { msg = JSON.parse(ev.data); } catch { return; }
    if (!msg || !msg.type) return;
    if (msg.type === "invalidate") {
      const topics = msg.topics || [];
      subs.forEach((sub) => { if (topics.some((t) => sub.topics.has(t))) { try { sub.cb(); } catch { /* noop */ } } });
    } else {
      eventSubs.forEach((cb) => { try { cb(msg); } catch { /* noop */ } });
    }
  };
  s.onclose = () => { cleanupTimers(); if (socket === s) socket = null; scheduleReconnect(); };
  s.onerror = () => { try { s.close(); } catch { /* noop */ } };
}

function maybeClose() {
  if (!hasWork() && socket) { try { socket.close(); } catch { /* noop */ } socket = null; cleanupTimers(); }
}

export function sendRealtime(obj) {
  if (socket && socket.readyState === WebSocket.OPEN) {
    try { socket.send(JSON.stringify({ ...obj, cid: CLIENT_ID })); } catch { /* noop */ }
  }
}

export function useRealtime(topics, cb) {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  const key = [...topics].sort().join(",");
  useEffect(() => {
    const sub = { topics: new Set(topics), cb: () => cbRef.current && cbRef.current() };
    subs.add(sub);
    connect();
    return () => { subs.delete(sub); maybeClose(); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useRealtimeChannel(cb) {
  const cbRef = useRef(cb);
  cbRef.current = cb;
  useEffect(() => {
    const sub = (msg) => cbRef.current && cbRef.current(msg);
    eventSubs.add(sub);
    connect();
    return () => { eventSubs.delete(sub); maybeClose(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
